import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { IconArrowDown, IconDeleteRed } from '../../../assets/images';
import {
    deleteChannelSelection,
    getSelectedChannels,
    toggleChannelSelection,
    useChannelNav,
    useChannelStatus,
    useIsChannelSelected,
    useMessageMode,
    useRecentChannelSearchText,
    useUserRole,
} from '../../../slice';
import { ImageCreateChannel } from '../../rootAccess';
import { handleChannelAction, resetAlertModal, setAlertModal, showInfoToast } from '../../utils';
import { ChannelMuteStatus, ChannelName, ChannelProfileImage } from '../ChannelProfileInfo';
import MenuButton from '../MenuButton';
import { ChannelUnreadCount } from '../ChannelUnread';
import { MarkAsReadUnreadAction, MuteAction, PinAction } from './RecentChannelAction';
import { RecentChannelLastMessage } from './RecentChannelLastMessage';

// Constants
const MESSAGE_MODE_RESTRICTED = 4;
const MESSAGE_MODE_PARTIAL = 2;
const USER_ROLE_MEMBER = 3;
const USER_ROLE_NONE = 0;
const ACTIVE_STATUS_DELETED = 'DELETED';
const ACTIVE_STATUS_INACTIVE = 'INACTIVE';

function RecentChannelItem({ scopeKey, channel, isSelectable = false, onClick, maxSelectionCount, index }) {
    const dispatch = useDispatch();
    const isChecked = useIsChannelSelected(channel.channelId);
    const activeStatus = useChannelStatus(channel.channelId);
    const userRole = useUserRole(channel.channelId);
    const messageMode = useMessageMode(channel.channelId);
    const searchText = useRecentChannelSearchText(scopeKey);
    const isModel = scopeKey === 'modal';

    const messageDisabled =
        (messageMode === MESSAGE_MODE_RESTRICTED && userRole === USER_ROLE_MEMBER) ||
        (messageMode === MESSAGE_MODE_PARTIAL && userRole === USER_ROLE_MEMBER);
    const removechannel = activeStatus === ACTIVE_STATUS_DELETED;
    const filterChannel = removechannel || userRole === USER_ROLE_NONE || activeStatus === ACTIVE_STATUS_INACTIVE;

    const activechannel = useChannelNav();
    const activeChannelId = activechannel?.channelId ?? activechannel?.id ?? null;
    const isActive = activeChannelId != null && String(activeChannelId) === String(channel.channelId);

    React.useEffect(() => {
        if (filterChannel || messageDisabled) {
            dispatch(deleteChannelSelection(channel));
        }
    }, [dispatch, channel, filterChannel, messageDisabled]);

    const handleSelect = useCallback(
        e => {
            e?.stopPropagation?.();
            const selectedChannels = getSelectedChannels();
            const isAlreadySelected = selectedChannels[channel.channelId];

            if (!isAlreadySelected && Object.keys(selectedChannels).length >= maxSelectionCount) {
                return showInfoToast(`You can only forward up to ${maxSelectionCount} channels`);
            }

            dispatch(toggleChannelSelection(channel));
        },
        [channel, maxSelectionCount, dispatch],
    );

    const handleChannelClick = useCallback(() => {
        if (!isSelectable) {
            return onClick?.(channel);
        }

        if (
            isSelectable &&
            userRole === USER_ROLE_MEMBER &&
            (messageMode === MESSAGE_MODE_PARTIAL || messageMode === MESSAGE_MODE_RESTRICTED)
        ) {
            return showInfoToast('User may not allow to send message in this channel');
        }

        handleSelect();
    }, [isSelectable, onClick, channel, userRole, messageMode, handleSelect]);

    const messageActionMenu = useMemo(
        () =>
            [
                userRole !== USER_ROLE_NONE && !filterChannel && (
                    <MuteAction channelId={channel.channelId} key="muteAction" />
                ),
                <PinAction channelId={channel.channelId} key="pinAction" />,
                userRole !== USER_ROLE_NONE && !removechannel && (
                    <MarkAsReadUnreadAction channelId={channel.channelId} key="markAsReadUnreadAction" />
                ),
                {
                    icon: <IconDeleteRed style={{ width: '18px', height: '18px' }} />,
                    label: 'Delete',
                    className: 'remove',
                    formatter: () => {
                        setAlertModal({
                            visible: true,
                            title: 'Are you sure you want to delete this channel?',
                            yesButton: 'Delete',
                            noButton: 'Cancel',
                            yesAction: () => {
                                handleChannelAction(channel.channelId, 'non member delete');
                            },
                            noAction: () => resetAlertModal(),
                            onRequestClose: () => resetAlertModal(),
                        });
                    },
                },
            ].filter(Boolean),
        [userRole, filterChannel, channel.channelId],
    );

    if (isModel && filterChannel) {
        return null;
    }

    return (
        <div className={`channels-list ${isActive ? 'active' : ''}`} key={channel.channelId}>
            <button className="channels-list-item" onClick={handleChannelClick}>
                <div className="channel-image">
                    <ChannelProfileImage
                        channelId={channel.channelId}
                        defaultImg={ImageCreateChannel}
                        className="channel-image-preview"
                        alt={channel.name || 'Channel Image'}
                    />
                </div>
                <div className="channel-details">
                    <ChannelName channelId={channel.channelId} className="channel-name" searchText={searchText} />
                    <RecentChannelLastMessage channelId={channel.channelId} />
                    {/* <ChannelStatus channelId={channel.channelId} /> */}
                </div>
                {!isModel && (
                    <>
                        <PinAction channelId={channel.channelId} defaultRender={false} />
                        <ChannelMuteStatus channelId={channel.channelId} defaultRender={false} />
                    </>
                )}
                <ChannelUnreadCount channelId={channel.channelId} />
                {isSelectable && (
                    <input
                        type="checkbox"
                        className="channel-checkbox"
                        checked={isChecked}
                        disabled={messageDisabled}
                    />
                )}
            </button>

            {messageActionMenu.length > 0 && scopeKey === 'page' && (
                <MenuButton
                    items={messageActionMenu}
                    trigger={<IconArrowDown />}
                    scrollContainerId="recent-channel-list"
                    index={index}
                    preferred="left"
                />
            )}
            {/* <span className='time_channel'>2:20 pm</span> */}
        </div>
    );
}

RecentChannelItem.propTypes = {
    channel: PropTypes.object.isRequired,
    onClick: PropTypes.func,
    isSelectable: PropTypes.bool,
    maxSelectionCount: PropTypes.number,
    scopeKey: PropTypes.string,
    index: PropTypes.number,
};

export default RecentChannelItem;
